import { useState } from "react";
import { toast } from "sonner";
import { Trash2, PlayCircle, CalendarClock, Volume2, VolumeX, Palette } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { deleteTemplate } from "@/lib/api";
import { TONE_LABELS } from "@/lib/beeper";

const TONE_LABEL_MAP = TONE_LABELS.reduce((acc, t) => {
  acc[t.v] = t.l;
  return acc;
}, {});

const MAX_TEMPLATES = 5;

export const TemplateList = ({
  templates,
  onChanged,
  onFlashNow,
  onSchedule,
}) => {
  const { t } = useI18n();
  const [busyId, setBusyId] = useState(null);

  const handleDelete = async (id) => {
    try {
      setBusyId(id);
      await deleteTemplate(id);
      toast.success(t("tpl.deleted"));
      onChanged?.();
    } catch (e) {
      toast.error(t("tpl.failedDelete"));
    } finally {
      setBusyId(null);
    }
  };

  const limitLine = t("tpl.limitHint")
    .replace("{count}", String(templates.length))
    .replace("{max}", String(MAX_TEMPLATES));

  return (
    <section
      data-testid="template-list"
      className="bg-white brutal-border brutal-shadow p-6 md:p-8 mt-6"
    >
      <div className="flex items-end justify-between mb-5 gap-3 flex-wrap">
        <div>
          <div className="inline-block bg-[#00CCFF] brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest mb-2">
            {t("tpl.badge")}
          </div>
          <h2 className="font-display text-2xl md:text-3xl uppercase leading-none">
            {t("tpl.title")}
          </h2>
        </div>
        <span className="font-mono text-[10px] uppercase tracking-widest bg-black text-white brutal-border px-2 py-1">
          {limitLine}
        </span>
      </div>

      {templates.length === 0 ? (
        <div
          data-testid="template-empty"
          className="brutal-border border-dashed p-6 text-center font-mono text-xs md:text-sm uppercase text-neutral-500"
        >
          {t("tpl.empty")}
        </div>
      ) : (
        <ul className="space-y-3" data-testid="template-items">
          {templates.map((tpl) => {
            const toneLabel =
              tpl.alarm_tone && tpl.alarm_tone !== "off"
                ? TONE_LABEL_MAP[tpl.alarm_tone] || tpl.alarm_tone
                : null;
            return (
              <li
                key={tpl.id}
                data-testid={`template-${tpl.id}`}
                className="brutal-border p-4 bg-[#F2F2F0]"
              >
                <div className="flex items-start justify-between gap-3 mb-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="font-display uppercase text-lg leading-tight break-words">
                      {tpl.name}
                    </div>
                    <div className="font-mono text-xs text-neutral-700 mt-1 line-clamp-2">
                      {tpl.message}
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                      {toneLabel ? (
                        <span className="inline-flex items-center gap-1 bg-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                          <Volume2 className="w-3 h-3" strokeWidth={3} /> {toneLabel}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 bg-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                          <VolumeX className="w-3 h-3" strokeWidth={3} />{" "}
                          {t("list.chip.silent")}
                        </span>
                      )}
                      {Array.isArray(tpl.bg_colors) && tpl.bg_colors.length > 0 && (
                        <span className="inline-flex items-center gap-1 bg-white brutal-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest">
                          <Palette className="w-3 h-3" strokeWidth={3} />
                          {tpl.bg_colors.map((c) => (
                            <span
                              key={c}
                              className="inline-block w-3 h-3 brutal-border"
                              style={{ backgroundColor: c }}
                            />
                          ))}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    data-testid={`tpl-flash-${tpl.id}`}
                    onClick={() => onFlashNow?.(tpl)}
                    className="bg-[#E5FF00] brutal-border px-3 py-2 font-display uppercase text-xs brutal-shadow-sm brutal-interactive flex items-center gap-1"
                  >
                    <PlayCircle className="w-4 h-4" strokeWidth={3} />
                    {t("tpl.flashNow")}
                  </button>
                  <button
                    data-testid={`tpl-schedule-${tpl.id}`}
                    onClick={() => onSchedule?.(tpl)}
                    className="bg-black text-white brutal-border px-3 py-2 font-display uppercase text-xs brutal-shadow-sm brutal-interactive flex items-center gap-1"
                  >
                    <CalendarClock className="w-4 h-4" strokeWidth={3} />
                    {t("tpl.schedule")}
                  </button>
                  <button
                    data-testid={`tpl-delete-${tpl.id}`}
                    onClick={() => handleDelete(tpl.id)}
                    disabled={busyId === tpl.id}
                    title={t("tpl.delete")}
                    className="bg-white brutal-border w-9 h-9 flex items-center justify-center brutal-shadow-sm brutal-interactive disabled:opacity-50 ml-auto"
                  >
                    <Trash2 className="w-4 h-4" strokeWidth={3} />
                  </button>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

export default TemplateList;
